import React, { memo } from 'react';
import styled, { css } from 'styled-components';
import { withTheme } from '@/theme';

import { formatDuration } from '@/util/duration';
import useTimeIsAfter from '@/util/useTimeIsAfter';
import { useCurrentTime } from '@/util/timerHooks';

export const AdditionalInfoWrapper = styled.span`
  display: flex;
  margin-top: ${props => (props.preOpFocused || props.pacuFocused ? '0.25rem' : '0.25rem')};
  color: ${withTheme((theme, props) =>
    props.status === 'alert' ? theme.warningColor.string() : theme.textColor.string()
  )};
  overflow: hidden;

  justify-content: space-between;

  ${props =>
    props.miniView &&
    css`
      margin-top: 0;
    `}
`;

const Procedure = styled.div`
  display: inline-block;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
`;

export const AdditionalInfo = styled.div`
  white-space: nowrap;
  flex: 1 1 0;
  min-width: 0;
  color: ${withTheme((theme, props) =>
    props.isNote ? theme.warningColor.string() : theme.secondaryTextColor.string()
  )};

  font-size: ${props => (props.size === 'large' ? '1.75rem' : '1.125rem')};
  line-height: 1.1;
  display: flex;

  @media (max-height: 900px) {
    font-size: ${props => (props.size === 'large' ? '1.75rem' : '0.875rem')};
  }

  @media (max-height: 600px) {
    font-size: ${props => (props.size === 'large' ? '1.75rem' : '2vh')};
  }
`;

const InfoRight = styled(AdditionalInfo)`
  display: flex;
  white-space: nowrap;
  flex: 0 1 0;
  justify-content: flex-end;
  min-width: auto;
  opacity: ${props => (props.status === 'alert' ? 'initial' : '0.5')};

  ${props =>
    props.miniView &&
    css`
      justify-content: flex-start;
      font-size: 1rem;
    `}
`;

const PatientSlotAdditionalInfo = ({
  procedure,
  roomName,
  timeEntered,
  status,
  size,
  miniView,
  expectedExitTime,
  preOpFocused,
  pacuFocused,
  showPreOpNote,
  preOpNote,
  showPacuNote,
  pacuNote,
  showPatientNote,
  patientNote,
  showRoom,
  showDuration,
  compactView,
  showProcedureType,
}) => {
  const isDelayed = useTimeIsAfter(expectedExitTime);

  const note = showPreOpNote ? preOpNote : showPacuNote ? pacuNote : showPatientNote ? patientNote : null;

  return (
    <AdditionalInfoWrapper
      preOpFocused={preOpFocused}
      pacuFocused={pacuFocused}
      status={isDelayed ? 'alert' : null}
      size={size}
      miniView={miniView}
    >
      {(!miniView || showProcedureType) && (
        <AdditionalInfo size={size} isNote={note}>
          {(!note || showProcedureType) && !!procedure && <Procedure>{procedure}</Procedure>}
          {!!note && <Procedure>{note}</Procedure>}
        </AdditionalInfo>
      )}
      <InfoRight miniView={miniView} size={size} status={isDelayed ? 'alert' : null}>
        {showDuration ? (
          <Duration since={timeEntered} />
        ) : (
          timeEntered &&
          (compactView ? (
            roomName || ''
          ) : showRoom && roomName ? (
            <>
              In {roomName} for <Duration since={timeEntered} />
            </>
          ) : (
            <Duration since={timeEntered} />
          ))
        )}
      </InfoRight>
    </AdditionalInfoWrapper>
  );
};

export default PatientSlotAdditionalInfo;

const Duration = memo(
  function Duration({ since }) {
    // Use the proper useCurrentTime hook for duration calculations
    const currentTime = useCurrentTime({
      interval: 60000, // Update every minute for duration display
      precision: 'minute',
      batteryOptimized: true,
    });

    return <>{formatDuration(since, currentTime, true)}</>;
  },
  (prev, next) => prev.since === next.since
);
